import { useEffect, useState } from 'react';

import { PlayerComparisonCard } from './PlayerComparisonCard';
import { TransfersCard } from './TransfersCard';
import { TransferHitModal } from '@components/pick/TransferHitModal';
import { useMakeTransfers } from '@hooks/transfers/useMakeTransfers';
import { useRecommendedTransfers } from '@hooks/transfers/useRecommendedTransfers';
import { useRecommendedTransfersStore } from '@lib/transfers/store/useRecommendedTransfersStore';
import {
  deriveTransfersViewState,
  isTransfersVisible
} from '@lib/transfers/utils/transfers-view-state';
import { applyNegativeTotalGuardrail } from '@lib/transfers/utils/predicted-points';

export const Recommended = () => {
  const resetRecommendedTransfersStore = useRecommendedTransfersStore(
    (state) => state.reset
  );

  const {
    freeTransfers,
    remainingTransfers,
    transfersMade,
    isAllowanceKnown,
    isUnlimited,
    transferRecommendations,
    isLoading,
    isError,
    refusalDescription,
    isTransferRecommendationsSuccess: hasLoaded,
    regenerate,
    refetch
  } = useRecommendedTransfers();

  // A set whose own total reads negative is withheld here, at the data
  // boundary, so the card, the comparison card and the make-transfers action
  // all see the same empty set and fall into the existing empty state. A
  // wildcard or free hit the member has activated is exempt, so the chip's
  // rebuilt squad is still shown; `isUnlimited` is what says they activated it,
  // since the solver can recommend a chip the planner does not hold.
  const transfers = applyNegativeTotalGuardrail(
    transferRecommendations?.data.transfers ?? [],
    transferRecommendations?.data.recommendedChip ?? null,
    isUnlimited
  );

  // Which transfer the comparison card is inspecting; rows in the transfers
  // list drive it. Reset whenever a new set of recommendations lands
  // (regenerate, filter change) so the card can't point at a stale index.
  const [selectedIndex, setSelectedIndex] = useState(0);
  useEffect(() => {
    setSelectedIndex(0);
  }, [transferRecommendations]);

  const viewState = deriveTransfersViewState({
    isLoading,
    isError,
    hasLoaded,
    hasTransfers: transfers.length > 0,
    freeTransfers,
    remainingTransfers,
    isUnlimited,
    mode: 'recommended'
  });

  const handleRegenerate = () => {
    resetRecommendedTransfersStore();
    regenerate();
    refetch();
  };

  // The cap above means a recommendation cannot exceed the allowance, so this
  // warning should never fire on this tab. It is kept as defence in depth
  // against a solver that returns more than it was asked for, and because the
  // custom tab — which shares this hook — genuinely can exceed. Do not delete
  // it on the grounds that it looks unreachable.
  const {
    makeTransfers,
    isMakingTransfers,
    transferHit,
    isHitWarningOpen,
    confirmHit,
    dismissHit
  } = useMakeTransfers({
    transfers,
    // Null means "no hit is possible": unlimited by chip, or an allowance we
    // could not read, which must not be priced as though it were zero.
    freeTransfers: isUnlimited || !isAllowanceKnown ? null : freeTransfers,
    transfersAlreadyMade: transfersMade,
    onCommitted: resetRecommendedTransfersStore
  });

  return (
    <div className="flex flex-col gap-5" data-cy="recommended-view">
      <TransfersCard
        mode="recommended"
        transfers={transfers}
        isLoading={isLoading}
        isError={isError}
        refusalDescription={refusalDescription}
        hasLoaded={hasLoaded}
        freeTransfers={freeTransfers}
        remainingTransfers={remainingTransfers}
        isUnlimited={isUnlimited}
        selectedIndex={selectedIndex}
        onSelect={setSelectedIndex}
        onRegenerate={handleRegenerate}
        onCommitRegenerate={regenerate}
        onMakeTransfers={makeTransfers}
        isMakingTransfers={isMakingTransfers}
      />
      {isTransfersVisible(viewState) && (
        <PlayerComparisonCard
          transfers={transfers}
          selectedIndex={selectedIndex}
        />
      )}
      <TransferHitModal
        isOpen={isHitWarningOpen}
        hit={transferHit}
        onConfirm={confirmHit}
        onDismiss={dismissHit}
        isConfirming={isMakingTransfers}
      />
    </div>
  );
};
